import { Quote, ArrowUpRight, Phone, CheckCircle2, Ruler, Award, Layers, Palette, Recycle, FileSignature } from "lucide-react";
const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const NAVY_DEEP = "#040A1F";
const GOLD = "#F5A623";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type Diff = {
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  body: string;
  badge?: string;
};
const DIFFERENTIATORS: Diff[] = [{
  icon: Ruler,
  title: "Free On-Site Measure, Always",
  body: "Phone quotes are guesses. We measure your opening with a laser, check structural clearance, electrical, and finish requirements. No charge, no obligation, no exceptions — the only honest way to quote a door.",
  badge: "Free"
}, {
  icon: Award,
  title: "Genuine OEM Doors Only",
  body: "We supply factory-direct doors from B&D, Steel-Line, Gliderol, Centurion, 4D Industrial, Hörmann. No grey-market imports, no rebranded knockoffs. Every door comes with full manufacturer warranty in your name.",
  badge: "OEM"
}, {
  icon: Layers,
  title: "Hidden Steel Reinforcement as Standard",
  body: "We install steel reinforcement struts on every sectional door — even when manufacturers list them as optional add-ons. It's the invisible difference between a door that sags at year five and one that doesn't.",
  badge: "Standard"
}, {
  icon: Palette,
  title: "Colour-Matched Trim & Weather Seal",
  body: "Reveal trim, weather seals, and finishing flashings colour-matched to your door. Most installers skip this and leave silver aluminium edges. We don't — it's the detail that separates trade work from a finished result."
}, {
  icon: Recycle,
  title: "Old Door Removal & Disposal Included",
  body: "We remove your existing door, drum-roll the cables safely, and dispose of all old materials properly. No hire skip needed, no extra charges, no mess for you to clean up after we leave."
}, {
  icon: FileSignature,
  title: "Workmanship Warranty in Writing",
  body: "On top of the manufacturer's warranty (typically 5–10 years on the door, 2–5 years on the motor), we add a 12-month written workmanship warranty. If anything we touched fails, we come back free of charge.",
  badge: "12 Months"
}];
export default function WhyChooseInstallation() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY
  }} aria-labelledby="why-choose-installation-heading">
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
      background: `radial-gradient(ellipse at 15% 30%, ${NAVY_LIGHT}66 0%, transparent 50%), radial-gradient(ellipse at 85% 80%, ${NAVY_DEEP} 0%, transparent 60%)`
    }} />

      <div aria-hidden className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.9 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD}66 50%, transparent 100%)`
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-20 md:pt-28 pb-20 md:pb-24">

        {/* Top strip */}
        <div className="flex items-center justify-between gap-6 mb-10 md:mb-12 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border" style={{
            borderColor: `${GOLD}66`,
            backgroundColor: `${GOLD}14`
          }}>
              <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.5} style={{
              color: GOLD
            } as React.CSSProperties} />
            </span>
            <span className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{
            color: GOLD
          }}>
              Why Gold for Installations
            </span>
            <span className="hidden md:block h-px w-12" style={{
            backgroundColor: `${GOLD}40`
          }} />
          </div>
          <div className="text-[10px] font-semibold tracking-[0.22em] uppercase" style={{
          color: "rgba(255,255,255,0.4)"
        }}>
            6 Standards · Every Install · No Shortcuts
          </div>
        </div>

        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-10 md:mb-12 items-start">
          <div className="lg:col-span-7">
            <div className="flex items-stretch gap-4">
              <div aria-hidden className="w-[3px] flex-shrink-0" style={{
              background: `linear-gradient(180deg, ${GOLD} 0%, ${GOLD}26 100%)`
            }} />
              <div>
                <Quote className="h-7 w-7 mb-2" strokeWidth={1.5} style={{
                color: GOLD
              } as React.CSSProperties} />
                <h2 id="why-choose-installation-heading" className="font-bold text-white" style={{
                fontFamily: FONT_HEAD,
                fontSize: "clamp(1.75rem, 3.6vw, 2.75rem)",
                lineHeight: 1.1,
                letterSpacing: "-0.025em"
              }}>
                  Half the install is the{" "}
                  <span style={{
                  color: GOLD
                }}>detail you don&rsquo;t see.</span>
                </h2>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <p className="text-[15px] leading-[1.6] mb-5" style={{
            color: "rgba(255,255,255,0.72)"
          }}>
              Cheap installers skip the things that don&rsquo;t show on day one.
              Five years later they show every day &mdash; sagging panels, leaking
              seals, dragging doors. These six standards apply to every install
              we do.
            </p>

            <a href="tel:0485000933" className="group inline-flex items-center gap-3 text-[14px] font-bold transition-colors" style={{
            color: "white",
            fontFamily: FONT_BODY
          }} onMouseEnter={e => e.currentTarget.style.color = GOLD} onMouseLeave={e => e.currentTarget.style.color = "white"}>
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border" style={{
              borderColor: `${GOLD}66`,
              backgroundColor: `${GOLD}1F`,
              color: GOLD
            }}>
                <Phone className="h-4 w-4" strokeWidth={2.5} />
              </span>
              Talk to an installer &mdash; 0485 000 933
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
            </a>
          </div>
        </div>

        {/* Section label */}
        <div className="text-[10px] font-bold tracking-[0.32em] uppercase mb-6" style={{
        color: "rgba(255,255,255,0.4)"
      }}>
          &mdash; The six standards
        </div>

        {/* 6-card grid (2 cols) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {DIFFERENTIATORS.map((diff, idx) => <DiffCard key={diff.title} diff={diff} index={idx} />)}
        </div>
      </div>

      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD}66 50%, transparent 100%)`
    }} />
    </section>;
}
function DiffCard({
  diff,
  index
}: {
  diff: Diff;
  index: number;
}) {
  const Icon = diff.icon;
  return <article className="group relative rounded-2xl border overflow-hidden transition-all duration-300" style={{
    backgroundColor: "rgba(255,255,255,0.025)",
    borderColor: "rgba(255,255,255,0.08)"
  }} onMouseEnter={e => {
    e.currentTarget.style.borderColor = `${GOLD}66`;
    e.currentTarget.style.backgroundColor = "rgba(245,166,35,0.04)";
    e.currentTarget.style.transform = "translateY(-2px)";
  }} onMouseLeave={e => {
    e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.025)";
    e.currentTarget.style.transform = "translateY(0)";
  }}>
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD} 50%, transparent 100%)`
    }} />

      <div aria-hidden className="absolute -bottom-3 -right-1 font-extrabold leading-none select-none pointer-events-none" style={{
      fontFamily: FONT_HEAD,
      color: `${GOLD}0F`,
      fontSize: "clamp(4rem, 7vw, 5.5rem)",
      letterSpacing: "-0.04em",
      fontVariantNumeric: "tabular-nums"
    }}>
        {String(index + 1).padStart(2, "0")}
      </div>

      <div className="relative p-5 md:p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-xl transition-all duration-300 group-hover:scale-105" style={{
          backgroundColor: `${GOLD}1F`,
          border: `1px solid ${GOLD}40`
        }}>
            <Icon className="h-[19px] w-[19px]" strokeWidth={2} style={{
            color: GOLD
          } as React.CSSProperties} />
          </div>

          {diff.badge && <span className="inline-flex items-center text-[9px] font-extrabold tracking-[0.18em] uppercase rounded-full px-2.5 py-1 border" style={{
          color: GOLD,
          backgroundColor: `${GOLD}0F`,
          borderColor: `${GOLD}40`,
          fontFamily: FONT_BODY
        }}>
              {diff.badge}
            </span>}
        </div>

        <h3 className="font-extrabold text-white mb-2.5 transition-colors duration-200 group-hover:text-[#F5A623]" style={{
        fontFamily: FONT_HEAD,
        fontSize: "clamp(1.0625rem, 1.5vw, 1.1875rem)",
        lineHeight: 1.2,
        letterSpacing: "-0.018em"
      }}>
          {diff.title}
        </h3>

        <p className="text-[13.5px] leading-[1.6]" style={{
        color: "rgba(255,255,255,0.68)"
      }}>
          {diff.body}
        </p>
      </div>
    </article>;
}